import {
  DEFAULT_MEMBER_FEATURE_FLAGS,
  DEFAULT_MEMBER_GROUPS,
  DEFAULT_PROFILE_DETAILS,
  FETCH_MEMBERS_PER_PAGE_COUNT,
  MEMBERS_HEADER
} from '@bo-constants/member/members';
import type {
  FormattedMemberGroups,
  FormattedMembersResponse,
  MemberAccountDetails,
  MemberAccountInformation,
  MemberDeactivationReason,
  MemberGeneralInformation,
  MemberHeaderInformation,
  MemberIpInformation,
  MemberOverlayInfo,
  MemberProfile,
  MemberResponse,
  MemberUserInformation,
  MembersFeatureFlag,
  MembersRequest,
  MembersResponse,
  SearchFilters
} from '@library/types/members';
import {
  convertToMonthDayYearFormat,
  convertToYearMonthDayFormat
} from '@library/utils/dateValidations';
import {
  dateDisplay,
  dateOfBirthDisplay
} from '@bo-utils/members/memberCommon';
import { removeUnderscore, toUSD } from '@bo-utils/common';
import type { ComboboxOptions } from '@library/components/selects';
import { DateTime } from 'luxon';
import { KINDBRIDGE_NOT_ENROLLED } from '@bo-constants/member/memberCopy';
import { formatPhoneNumber } from '@library/component-utils/input';
import { getLimits } from '@bo-utils/members/memberResponsibleGaming';

// Default feature flag values
export const memberFeatureFlags: MembersFeatureFlag = {
  ...DEFAULT_MEMBER_FEATURE_FLAGS
};

export const setMemberFeatureFlag = (
  flags: Partial<MembersFeatureFlag>
): void => {
  Object.entries(flags).forEach(([key, value]) => {
    if (key in memberFeatureFlags) {
      memberFeatureFlags[key as keyof MembersFeatureFlag] = value;
    }
  });
};

export const transformMembers = ({
  featureFlags,
  page,
  perPage,
  response
}: {
  featureFlags: MembersFeatureFlag;
  page: number;
  perPage: number;
  response: MembersResponse | undefined;
}): FormattedMembersResponse | undefined => {
  if (!response) {
    return undefined;
  }

  setMemberFeatureFlag(featureFlags);

  const { included } = response;

  const { isRulesetRevampActive } = featureFlags;

  const formattedMembers = response.data?.map((user) => {
    const { relationships } = user;

    const memberGroups = relationships?.user_groups?.data.map(
      ({ id }) => included.find((group) => group.id === id)?.attributes.name
    ) as Array<string>;

    const {
      attributes: {
        created_at: joinDate,
        date_of_birth: dateOfBirth,
        deactivated,
        email,
        full_name: fullName,
        ruleset_name: rulesetName = ''
      },
      id,
      type
    } = user;

    const rulesets = isRulesetRevampActive ? memberGroups : [rulesetName];

    return {
      active: !deactivated,
      dateOfBirth,
      email,
      fullName,
      id,
      joinDate,
      rulesets,
      type
    };
  });

  const totalPages =
    formattedMembers.length < (perPage || FETCH_MEMBERS_PER_PAGE_COUNT)
      ? page
      : page + 1;

  return {
    currentPage: page,
    members: formattedMembers,
    totalPages
  };
};

const getVerificationClassName = ({ status }: { status: string }): string => {
  const className = 'capitalize';

  switch (status) {
    case 'passed':
      return `${className} text-atlien-200`;
    case 'failed':
      return `${className} text-roses`;
    default:
      return className;
  }
};

export const transformMemberResponse = ({
  featureFlags,
  memberResponse
}: {
  featureFlags: MembersFeatureFlag;
  memberResponse: MemberResponse | undefined;
}): MemberProfile | undefined => {
  if (!memberResponse) {
    return undefined;
  }

  setMemberFeatureFlag(featureFlags);

  const {
    data: { attributes, id, relationships },
    included
  } = memberResponse;

  const { isRulesetRevampActive } = featureFlags;

  const isResponsibleGaming = included.some((data) =>
    ['self_exclusion', 'self_timeout', 'limit'].includes(data.type)
  );

  const memberGroups = included
    .filter(({ type }) => type === 'user_group')
    .map(({ attributes: { name }, id: groupId }) => ({
      id: groupId,
      name
    })) as FormattedMemberGroups;

  const memberGroupsValue = memberGroups?.length
    ? memberGroups
    : DEFAULT_MEMBER_GROUPS;

  const memberGroupNamesValue = memberGroupsValue
    .map(({ name }) => String(name))
    .join(', ');

  const isDeactivated = attributes.deactivated;

  const rulesets = isRulesetRevampActive
    ? {
        label: 'Groups',
        tooltip:
          memberGroupsValue.length > 1
            ? memberGroupsValue.slice(1).map(({ name }) => String(name))
            : undefined,
        value: memberGroupsValue[0]?.name
      }
    : {
        label: 'Ruleset',
        value: attributes.deactivated ? 'Deactivated' : attributes.ruleset_name
      };

  const vipHostRel = relationships?.vip_assignments?.data[0]?.id;
  const vipHostId = vipHostRel
    ? included
        .find((include) => include.id === vipHostRel)
        ?.attributes?.admin_user_id?.toString()
    : undefined;

  const header: MemberHeaderInformation = {
    credit: {
      label: 'Credit',
      value: toUSD(attributes.credit ?? 0)
    },
    fullName: {
      label: 'Full Name',
      value: attributes.full_name
    },
    email: {
      label: 'Email',
      value: attributes.email
    },
    promo: {
      label: 'Promo',
      value: toUSD(attributes.promo ?? 0)
    },
    rulesets,
    responsibleGaming: {
      label: 'Responsible Gaming',
      value: isResponsibleGaming
    },
    rpu: {
      label: 'RPU',
      value: attributes.rpu
    },
    state: {
      label: 'State',
      value: attributes.state
    },
    vip: {
      label: 'VIP',
      value: attributes.vmp_member
    },
    vipHostId: {
      label: 'VIP Host ID',
      value: vipHostId
    }
  };

  const dateOfBirth = attributes.date_of_birth
    ? dateDisplay(attributes.date_of_birth)
    : undefined;

  const copiedDateOfBirth = dateOfBirthDisplay(
    String(attributes.date_of_birth)
  );

  const profileRelId = relationships?.profile?.data?.id;
  const profileAttributes = profileRelId
    ? included.find((include) => include.id === profileRelId)!.attributes
    : DEFAULT_PROFILE_DETAILS;

  /* The sequence/order of these attributes is intentional for display order */
  const generalInfo: MemberGeneralInformation = {
    deactivationReason: {
      className: getVerificationClassName({ status: 'failed' }),
      isHidden: !isDeactivated,
      label: 'Deactivation Reason',
      value: removeUnderscore(
        attributes.deactivation_reason || 'Self-Deactivated'
      )
    },
    userId: {
      label: 'User ID',
      value: id
    },
    uuid: {
      label: 'UUID',
      value: attributes.uuid
    },
    name: {
      label: 'Name',
      value: attributes.full_name
    },
    emailAddress: {
      dataSentryMask: true,
      label: 'Email',
      value: attributes.email
    },
    dateOfBirth: {
      copyValue: copiedDateOfBirth,
      dataSentryMask: true,
      label: 'Birth Date',
      value: dateOfBirth || ''
    },
    idologyVerification: {
      className: getVerificationClassName({
        status: attributes.idology_validation_state || ''
      }),
      label: 'Idology Verification',
      value: attributes.idology_validation_state
    },
    socureVerification: {
      className: getVerificationClassName({
        status: attributes.socure_validation_state || ''
      }),
      label: 'Socure Verification',
      value: attributes.socure_validation_state
    },
    internalVerification: {
      className: getVerificationClassName({
        status: attributes.internal_validation_state || ''
      }),
      label: 'Internal Verification',
      value: attributes.internal_validation_state
    },
    referralCode: {
      label: 'Referral code',
      value: attributes.referral_code
    },
    inviteCode: {
      label: 'Invite code',
      value: attributes.invite_code
    },
    ftdPromoType: {
      className: 'capitalize',
      label: 'New Member Offer Type',
      value: removeUnderscore(attributes.ftd_promo_type ?? '')
    },
    accountCreationDate: {
      label: 'Account Created Date',
      value: dateDisplay(attributes.created_at, true)
    },
    paymentTreatment: {
      className: 'capitalize',
      label: 'Payment Treatment',
      value: attributes.payment_service
    },
    pushNotificationToken: {
      label: 'Push Notification Token',
      value: attributes.push_notification_token
    },
    kindbridgeEnrollment: {
      label: 'Kindbridge Enrollment',
      value: attributes.kindbridge_enrolled_at
        ? dateDisplay(attributes.kindbridge_enrolled_at, true)
        : KINDBRIDGE_NOT_ENROLLED
    }
  };

  const accountDetails: MemberAccountDetails = {
    rulesets: {
      className: 'capitalize',
      isHidden: !isRulesetRevampActive,
      label: 'Member Groups',
      value: memberGroupNamesValue
    },
    profileUsername: {
      label: 'Display name',
      value: profileAttributes.username ?? ''
    },
    profileVisibility: {
      className: 'capitalize',
      label: 'Profile Visibility',
      value: profileAttributes.visibility?.[0] ?? 'private'
    },
    remainingPlayThrough: {
      label: 'Remaining Playthrough',
      value: attributes.remaining_playthrough
    },
    amountEntered: {
      label: 'Amount Entered',
      value: attributes.amount_entered
    },
    lifeTimeDeposits: {
      label: 'Lifetime Deposits',
      value: attributes.lifetime_deposits
    },
    lifeTimeWithdraws: {
      label: 'Lifetime Withdrawls',
      value: attributes.lifetime_withdrawals
    },
    promoBalance: {
      label: 'Promo Balance',
      value: attributes.promo
    },
    creditBalance: {
      label: 'Credit Balance',
      value: attributes.credit
    },
    totalListingsPlaced: {
      label: 'Total Listings Placed',
      value: attributes.number_of_entries
    },
    totalListingsWon: {
      label: 'Total Listings Won',
      value: attributes.entries_won
    },
    totalAmountWon: {
      label: 'Total Amount Won',
      value: attributes.amount_won
    }
  };

  const userInfo: MemberUserInformation = {
    streetAddress: {
      dataSentryMask: true,
      label: 'Street Address',
      value: attributes.address
    },
    city: {
      dataSentryMask: true,
      label: 'City',
      value: attributes.city
    },
    state: {
      dataSentryMask: true,
      label: 'State',
      value: attributes.state
    },
    zipCode: {
      dataSentryMask: true,
      label: 'Zip Code',
      value: attributes.postal_code
    }
  };

  const ipInfo: MemberIpInformation = {
    currentSignInIp: {
      dataSentryMask: true,
      label: 'Current Sign In IP',
      value: attributes.current_sign_in_ip
    },
    lastSignInIp: {
      dataSentryMask: true,
      label: 'Last Sign In IP',
      value: attributes.last_sign_in_ip
    },
    signUpIp: {
      dataSentryMask: true,
      label: 'Sign Up IP',
      value: attributes.sign_up_ip
    }
  };

  const limits = getLimits({ memberIncluded: included });

  const rulesetInfo = {
    rulesetId: attributes.ruleset_id,
    rulesetName: attributes.ruleset_name
  };

  const accountInformation: MemberAccountInformation = {
    address: attributes.address,
    city: attributes.city,
    dateOfBirth: convertToMonthDayYearFormat(attributes.date_of_birth ?? ''),
    email: attributes.email,
    firstName: attributes.first_name,
    inviteCode: attributes.invite_code ?? '',
    lastName: attributes.last_name,
    phoneNumber: attributes.phone_number
      ? formatPhoneNumber(attributes.phone_number)
      : '',
    postalCode: attributes.postal_code,
    state: attributes.state,
    username: profileAttributes.username ?? '',
    verified: profileAttributes.verified ?? false
  };

  const memberOverlayInfo: MemberOverlayInfo = {
    accountInformation,
    isDeactivated: attributes.deactivated || false,
    isVipMember: attributes.vmp_member || false,
    memberId: id,
    memberEmail: attributes.email,
    memberRulesetId: attributes.ruleset_id,
    memberRulesetName: attributes.ruleset_name,
    memberUuid: attributes.uuid ?? '',
    needsTid: attributes.needs_tid,
    tidValidation: attributes.tid_validation,
    vipHostAssignedId: vipHostId
  };

  return {
    accountDetails,
    generalInfo,
    header,
    ipInfo,
    limits,
    rulesetInfo,
    memberOverlayInfo,
    userInfo
  };
};

// Update date formats of MMDDYYYY & MM/DD/YYYY to MM-DD-YYYY
export const formatDateOfBirth = (dateOfBirth: string) => {
  const dateRegex =
    /^(0[1-9]|1[0-2])[/-]?(0[1-9]|[12][0-9]|3[01])[/-]?(\d{4})$/;
  const match = dateRegex.exec(dateOfBirth);

  if (match) {
    const [, month, day, year] = match;
    return `${month}-${day}-${year}`;
  }

  return dateOfBirth;
};

export const generateUrlWithSearchParams = (filters: SearchFilters): string => {
  const baseUrl = window.location.origin + window.location.pathname;
  const searchParams = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      const paramValue =
        value instanceof Date
          ? String(DateTime.fromJSDate(value).toISODate())
          : String(value);
      searchParams.append(key, paramValue);
    }
  });

  return searchParams.size ? `${baseUrl}?${searchParams.toString()}` : baseUrl;
};

export const getMemberDeactivationOptions = (): ComboboxOptions => {
  const reasons: Record<MemberDeactivationReason, string> = {
    chargebacks: 'Chargebacks',
    cooldown: 'Cooldown',
    cybersource: 'Cybersource',
    former_employee: 'Former Employee',
    fraud_ban: 'Fraud Ban',
    general: 'General',
    investigation: 'Investigation',
    multi_accounting: 'Multi Accounting',
    self_exclusion: 'Self Exclusion',
    tos_violation: 'TOS Violation',
    underage: 'Underage',
    verification_decline: 'Verification Decline'
  };

  return Object.keys(reasons).map((key) => ({
    value: key,
    label: reasons[key as MemberDeactivationReason]
  }));
};

export const getMembersTableHeader = () => {
  const { isRulesetRevampActive } = memberFeatureFlags;

  return [
    ...MEMBERS_HEADER,
    {
      column: isRulesetRevampActive ? 'member_groups' : 'ruleset',
      essential: true,
      label: isRulesetRevampActive ? 'Member groups' : 'Ruleset',
      sortEnabled: false
    }
  ];
};

export const getMembersRequestParams = (
  currentPage: number,
  memberSearchFilters: Partial<SearchFilters>
): MembersRequest => {
  const {
    address,
    currentSignInIp,
    dateOfBirth,
    email,
    firstName,
    inviteCode,
    joinDate,
    lastName,
    lastSignInIp,
    memberId,
    numberOfEntries,
    numberOfEntriesType,
    postalCode,
    pushNotificationToken,
    referralCode,
    state,
    uuid
  } = memberSearchFilters;

  const dateOfBirthFormat = convertToYearMonthDayFormat(dateOfBirth ?? '');
  const joinDateString = joinDate ? joinDate?.toISOString().slice(0, 10) : '';

  const numberOfEntriesValue = numberOfEntries
    ? Number(numberOfEntries)
    : undefined;

  const numberOfEntriesKeyMap: Record<string, string> = {
    eq: 'number_of_entries_eq',
    gt: 'number_of_entries_gt',
    lt: 'number_of_entries_lt'
  };

  const numberOfEntriesKey =
    numberOfEntriesType && numberOfEntriesKeyMap[numberOfEntriesType];

  return {
    address: address?.trim(),
    current_sign_in_ip: currentSignInIp?.trim(),
    date_of_birth: dateOfBirthFormat,
    email: email?.trim(),
    first_name: firstName?.trim(),
    invite_code: inviteCode?.trim(),
    created_at_after: joinDateString,
    created_at_before: joinDateString,
    last_name: lastName?.trim(),
    last_sign_in_ip: lastSignInIp?.trim(),
    id: memberId?.trim(),
    ...(numberOfEntriesKey &&
      numberOfEntriesValue && {
        [numberOfEntriesKey]: numberOfEntriesValue
      }),
    page: currentPage,
    per_page: FETCH_MEMBERS_PER_PAGE_COUNT,
    postal_code: postalCode?.trim(),
    push_notification_token: pushNotificationToken?.trim(),
    referral_code: referralCode?.trim(),
    state: state?.trim(),
    uuid: uuid?.trim()
  };
};
